import { Html, Line } from '@react-three/drei'
import * as THREE from 'three'
import { useMeasurementPresentation } from '../../Measurement'
import { optionalVehicleNumber } from '../../../lib/vehicleFormContract'
import type { Vehicle } from '../../../types/optimization'

type Point = [number, number, number]

function Dimension({ id, label, value, start, end, from, to }: {
  id: string; label: string; value: number; start: Point; end: Point; from: Point; to: Point
}) {
  const { format } = useMeasurementPresentation()
  const direction = new THREE.Vector3(...end).sub(new THREE.Vector3(...start)).normalize()
  const midpoint = new THREE.Vector3(...start).add(new THREE.Vector3(...end)).multiplyScalar(.5)
  const head = Math.min(.16, new THREE.Vector3(...start).distanceTo(new THREE.Vector3(...end)) / 5)
  const color = '#f97316'
  return <group name={`vehicle-dimension-${id}`}>
    <Line points={[from, start]} color={color} lineWidth={1} dashed dashSize={.08} gapSize={.06} depthTest={false} depthWrite={false} renderOrder={100} />
    <Line points={[to, end]} color={color} lineWidth={1} dashed dashSize={.08} gapSize={.06} depthTest={false} depthWrite={false} renderOrder={100} />
    <Line points={[start, end]} color={color} lineWidth={1.5} depthTest={false} depthWrite={false} renderOrder={100} />
    {[{ point: start, sign: -1 }, { point: end, sign: 1 }].map(({ point, sign }) => {
      const outward = direction.clone().multiplyScalar(sign)
      const center = new THREE.Vector3(...point).addScaledVector(outward, -head / 2)
      const rotation = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), outward)
      return <mesh key={sign} position={center} quaternion={rotation} renderOrder={101}>
        <coneGeometry args={[head * .32, head, 12]} />
        <meshBasicMaterial color={color} depthTest={false} depthWrite={false} />
      </mesh>
    })}
    <Html position={midpoint} center zIndexRange={[5, 0]} style={{ pointerEvents: 'none' }}>
      <span data-vehicle-dimension={id} aria-label={`${label} : ${format(value, 'mm', 'vehicleLength')}`} className="block whitespace-nowrap rounded-md border border-orange-200 bg-white/95 px-2 py-1 text-[11px] font-semibold tabular-nums text-orange-900 shadow-sm dark:border-orange-800 dark:bg-slate-950/95 dark:text-orange-200">{label} · {format(value, 'mm', 'vehicleLength')}</span>
    </Html>
  </group>
}

/** Cotes du volume configuré, indépendantes de la carrosserie et des badges. */
export function VehicleDimensions3D({ vehicle, isTractor }: { vehicle: Vehicle | null | undefined; isTractor: boolean }) {
  const length = optionalVehicleNumber(vehicle?.dimensions?.length ?? vehicle?.length)
  const width = optionalVehicleNumber(vehicle?.dimensions?.width ?? vehicle?.width)
  const height = optionalVehicleNumber(vehicle?.dimensions?.height ?? vehicle?.height)
  // Never label the preview's fallback geometry as a real measurement.
  if (length == null || width == null || height == null || Math.min(length, width, height) <= 0) return null
  const l = length / 1000, w = width / 1000, h = height / 1000
  const suffix = isTractor ? ' véhicule' : ' intérieure'
  return <group name="vehicle-dimensions">
    <Dimension id="length" label={`Longueur${suffix}`} value={length} from={[0, 0, w]} to={[l, 0, w]} start={[0, -1.2, w + .55]} end={[l, -1.2, w + .55]} />
    <Dimension id="width" label={`Largeur${suffix}`} value={width} from={[l, 0, 0]} to={[l, 0, w]} start={[l + .65, -.3, 0]} end={[l + .65, -.3, w]} />
    <Dimension id="height" label={`Hauteur${suffix}`} value={height} from={[l, 0, w]} to={[l, h, w]} start={[l + .65, 0, w + .55]} end={[l + .65, h, w + .55]} />
  </group>
}
